import React, { useState, useRef, useEffect } from "react";
import {
  Box,
  Modal,
  Grid,
  IconButton,
  useTheme,
  Typography,
} from "@mui/material";

function ScrollImageModal({ openModal, handleClose, images=null, sx, disabled=false, numImages=0, garmentUUID=null, groupRepresentativeBorder=false, groupRepresentativeBorderIndex=null, showGarmentDetails=true, garment=null, clickToClose=true, onPrev=null, onNext=null, canGoPrev=true, canGoNext=true }) {
  const theme = useTheme()
  
  const [garmentImages, setGarmentImages] = useState([])
  const imageRefs = useRef({}); // Store refs for all images
  
  const handleCloseRequest = (event, reason) => {
    event?.stopPropagation()
    if (disabled !== null && disabled !== undefined && disabled) return
    if (handleClose) handleClose(event, reason)
  }

  const handleContainerClick = (event) => {
    if (!clickToClose) return;
    
    // Check if the click target is an interactive element (text, button, etc.)
    const target = event.target;
    
    // Don't close if there's selected text (user is selecting text)
    if (window.getSelection && window.getSelection().toString().length > 0) {
      return;
    }
    
    // Check if clicking directly on text content (Typography elements)
    const isTextElement = 
      target.tagName === 'P' ||
      target.tagName === 'SPAN' ||
      (target.closest('[data-garment-details]') && 
       (target.tagName === 'P' || target.tagName === 'SPAN' || target.textContent.trim().length > 0));
    
    const isInteractiveElement = 
      target.closest('button') ||
      target.closest('[role="button"]') ||
      // Only prevent closing if clicking directly on text, not on empty space in the Box
      (isTextElement && target.textContent.trim().length > 0);
    
    // Close if not clicking on an interactive element (images and whitespace will close)
    if (!isInteractiveElement) {
      handleCloseRequest(event)
    }
  }
  
  // Scroll to the image after images are set and modal is open
  useEffect(() => {
    let timeoutId;
  
    if (
      groupRepresentativeBorderIndex != null &&
      openModal &&
      garmentImages.length > 0 &&
      imageRefs.current[groupRepresentativeBorderIndex]
    ) {
      timeoutId = setTimeout(() => {
        imageRefs.current[groupRepresentativeBorderIndex].scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      }, 400);
    }
  
    return () => clearTimeout(timeoutId); // Clean up timeout on unmount or re-run
  }, [openModal, garmentImages, groupRepresentativeBorderIndex]);

  useEffect(() => {
    if (images && images.length > 0)
    {
      setGarmentImages(images)
    } else if (garmentUUID && numImages && numImages > 0) {
      const gImages = Array.from({ length: numImages }, (_, i) => {
        return `https://scraped-garment-images.s3.eu-west-2.amazonaws.com/${
          garmentUUID
        }-${i + 1}.jpg`;
      });
      setGarmentImages(gImages)
    }
  }, [images, numImages, garmentUUID])

  return (
    <Modal
        open={disabled !== null && disabled !== undefined ? (!disabled ? openModal : null) : openModal} 
        onClose={disabled !== null && disabled !== undefined ? (!disabled ? handleCloseRequest : null) : handleCloseRequest}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
        sx={{overflowY: "scroll", width: "75%", height: "75%", backgroundColor: theme.palette.primary[100], margin: "auto", zIndex: 9999}}
      >
        <Grid container spacing={1} onClick={handleContainerClick} sx={{...sx, ...({backgroundColor: theme.palette.primary[100], p: 4, position: "relative"})}}>
            {onPrev && (
              <IconButton
                aria-label="Previous garment"
                onClick={(e) => {
                  e.stopPropagation()
                  if (canGoPrev) {
                    onPrev()
                  }
                }}
                disabled={!canGoPrev}
                sx={{
                  position: "fixed",
                  top: "50%",
                  left: "14%",
                  transform: "translateY(-50%)",
                  width: 40,
                  height: 40,
                  borderRadius: 2,
                  backgroundColor: canGoPrev ? ((theme.palette.primary && theme.palette.primary[50]) ? theme.palette.primary[50] : theme.palette.primary[100]) : theme.palette.primary[200],
                  border: `1px solid ${canGoPrev ? "black" : theme.palette.primary[400]}`,
                  color: canGoPrev ? "black" : theme.palette.primary[600],
                  opacity: canGoPrev ? 1 : 0.6,
                  cursor: canGoPrev ? "pointer" : "default",
                  zIndex: 11000,
                  "&:hover": {
                    backgroundColor: canGoPrev ? theme.palette.primary[300] : theme.palette.primary[200],
                  },
                }}
              >
                <Typography variant="body_sm" sx={{fontWeight: 800, fontSize: "1.25rem", lineHeight: 1, color: canGoPrev ? "black" : theme.palette.primary[600]}}>
                  {"<"}
                </Typography>
              </IconButton>
            )}
            {onNext && (
              <IconButton
                aria-label="Next garment"
                onClick={(e) => {
                  e.stopPropagation()
                  if (canGoNext) {
                    onNext()
                  }
                }}
                disabled={!canGoNext}
                sx={{
                  position: "fixed",
                  top: "50%",
                  right: "14%",
                  transform: "translateY(-50%)",
                  width: 40,
                  height: 40,
                  borderRadius: 2,
                  backgroundColor: canGoNext ? ((theme.palette.primary && theme.palette.primary[50]) ? theme.palette.primary[50] : theme.palette.primary[100]) : theme.palette.primary[200],
                  border: `1px solid ${canGoNext ? "black" : theme.palette.primary[400]}`,
                  color: canGoNext ? "black" : theme.palette.primary[600],
                  opacity: canGoNext ? 1 : 0.6,
                  cursor: canGoNext ? "pointer" : "default",
                  zIndex: 11000,
                  "&:hover": {
                    backgroundColor: canGoNext ? theme.palette.primary[300] : theme.palette.primary[200],
                  },
                }}
              >
                <Typography variant="body_sm" sx={{fontWeight: 800, fontSize: "1.25rem", lineHeight: 1, color: canGoNext ? "black" : theme.palette.primary[600]}}>
                  {">"}
                </Typography>
              </IconButton>
            )}
            <IconButton
              aria-label="Close"
              onClick={handleCloseRequest}
              sx={{
                position: "absolute",
                top: theme.spacing(4),
                right: theme.spacing(2),
                width: 40,
                height: 40,
                borderRadius: 2,
                backgroundColor: (theme.palette.primary && theme.palette.primary[50]) ? theme.palette.primary[50] : theme.palette.primary[100],
                border: `1px solid black`,
                color: theme.palette.primary[900],
                "&:hover": {
                  backgroundColor: theme.palette.primary[300],
                },
              }}
            >
              <Typography variant="body_sm" sx={{fontWeight: 800, fontSize: "1.25rem", lineHeight: 1, color: "black"}}>
                X
              </Typography>
            </IconButton>
            {showGarmentDetails && garment && 
            <Box 
              data-garment-details
              onMouseDown={(e) => {
                // Only stop propagation if clicking on text content, not empty space
                const target = e.target;
                if (target.tagName === 'P' || target.tagName === 'SPAN' || target.textContent.trim().length > 0) {
                  e.stopPropagation();
                }
              }}
              sx={{display: "flex", flexDirection: "column", gap: 1, width: "fit-content", marginTop: 0}}
            >
              {garmentUUID && 
              <Typography 
                variant="body_sm" 
                onMouseDown={(e) => e.stopPropagation()}
                sx={{fontSize: "1.25rem", textOverflow: "ellipsis", overflow: "hidden", whiteSpace: "nowrap", cursor: "text"}}
              >
                <b>ID:</b> {garmentUUID}
              </Typography>}
              {(garment?.BRAND || garment?.brand) && 
              <Typography 
                variant="body_sm" 
                onMouseDown={(e) => e.stopPropagation()}
                sx={{fontSize: "1.25rem", textOverflow: "ellipsis", overflow: "hidden", whiteSpace: "nowrap", cursor: "text"}}
              >
                <b>BRAND:</b> {garment?.brand || garment?.BRAND}
              </Typography>}
              {garment?.title && 
              <Typography 
                variant="body_sm" 
                onMouseDown={(e) => e.stopPropagation()}
                sx={{fontSize: "1.25rem", textOverflow: "ellipsis", overflow: "hidden", whiteSpace: "nowrap", cursor: "text"}}
              >
                <b>TITLE:</b> {garment?.title}
              </Typography>}
            </Box>}
            {garmentImages && garmentImages.length > 0 && garmentImages?.map((image, index) => (
            <Grid item xs={6}>
                <img
                ref={(el) => (imageRefs.current[index] = el)} // Set refs
                loading="eager"
                key={index}
                src={image}
                alt={`Garment Image ${index + 1}`}
                style={{ width: "100%", marginBottom: "10px",
                ...(groupRepresentativeBorder && index === groupRepresentativeBorderIndex && {
                  borderRadius: 4,
                  border: `8px solid ${theme.palette.listedPriceLine[400]}`
                })}}
                />
            </Grid>
            ))}
            {!garmentImages || garmentImages.length === 0 
            ? <Typography 
                variant="h4" 
                onMouseDown={(e) => e.stopPropagation()}
                sx={{color: theme.palette.primary[300], cursor: "text"}}
              >
                No images.
              </Typography>
            : <></>}
        </Grid>
    </Modal>
  );
}

export default ScrollImageModal;
